import { Injectable, Type } from '@angular/core';
import {
  ApiQuery,
  ApiResponse,
  BaseApiService,
} from '@kinet-open-network/client/core/api-client';
import { mapToModel } from '@kinet-open-network/client/shared/data-access/model';
import {
  downloadBlob,
  getFilenameFromHeaders,
} from '@kinet-open-network/client/shared/util';
import {
  DocumentMassDownloadFormat,
  IDocumentActionData,
  IDocumentSignatureReminderSettings,
  IDocumentTemplateOption,
  ISendDocumentOptions,
  SendDocMethod,
} from '@kinet-open-network/shared/data-access/model';
import { Observable, firstValueFrom, map } from 'rxjs';
import { Document } from '../models/document.model';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService extends BaseApiService<Document> {
  override model: Type<Document> = Document;

  override get endpoint() {
    return '/documents';
  }

  getTemplatesOptions() {
    return this.http
      .get<
        ApiResponse<IDocumentTemplateOption[]>
      >('/document-templates/options')
      .pipe(map((response) => response.data));
  }

  getTemplateOptionById(
    id: string,
  ): Observable<IDocumentTemplateOption | null> {
    return this.http
      .get<
        ApiResponse<IDocumentTemplateOption | null>
      >(`/document-templates/options/${id}`)
      .pipe(map((response) => response.data));
  }

  getPdf(id: string) {
    return firstValueFrom(
      this.http.get(`${this.endpoint}/${id}/download`, {
        responseType: 'blob',
        observe: 'response',
      }),
    );
  }

  printPdf(id: string) {
    return firstValueFrom(
      this.http.get(`${this.endpoint}/${id}/print`, {
        responseType: 'blob',
        observe: 'response',
      }),
    );
  }

  download(id: string, name?: string) {
    return this.getPdf(id).then((file) => {
      const blob = file.body as Blob;
      return downloadBlob(blob, name ?? 'download');
    });
  }

  print(id: string, name?: string) {
    return this.getPdf(id).then((file) => {
      const blob = file.body as Blob;
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    });
  }

  batchAction(
    data: Pick<IDocumentActionData, 'action'>,
    query?: ApiQuery | null,
  ) {
    return this.http.post<void>(`${this.endpoint}/batch-action`, data, {
      params: this.apiQueryToHttpParams(query),
    });
  }

  async massDownload(
    format: DocumentMassDownloadFormat,
    query?: ApiQuery | null,
  ): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/mass-download`, {
        params: this.apiQueryToHttpParams({ ...query, format }),
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const filename = getFilenameFromHeaders(response.headers);

    return downloadBlob(
      response.body as Blob,
      filename ?? `dokumenty.${format}`,
    );
  }

  signDocument(id: string, signature: string) {
    return this.http
      .post<Document>(`${this.endpoint}/${id}/sign`, { signature })
      .pipe(mapToModel(this.model));
  }

  markAsSigned(id: string) {
    return this.http
      .post<Document>(`${this.endpoint}/${id}/mark-signed`, {})
      .pipe(mapToModel(this.model));
  }

  sendDocument(
    id: string,
    method: SendDocMethod,
    options?: ISendDocumentOptions,
  ) {
    return this.http.post(`${this.endpoint}/${id}/send`, { method, options });
  }

  generate(id: string) {
    return this.http.post(`${this.endpoint}/${id}/generate`, {});
  }

  updateReminderSettings(
    id: string,
    settings: IDocumentSignatureReminderSettings | null,
  ) {
    return this.http.put<Document>(
      `${this.endpoint}/${id}/signature-reminder-settings`,
      { settings },
    );
  }

  /** Push a template's current reminder policy onto its unsigned documents. */
  syncReminderSettings(templateId: string) {
    return this.http.post<{ updated: number }>(
      `${this.endpoint}/sync-signature-reminder-settings`,
      { templateId },
    );
  }

  override findOne(id: string, token?: string): Observable<Document> {
    return this.http
      .get<ApiResponse<Document>>(`${this.endpoint}/${this.encodeId(id)}`, {
        params: token ? { token } : {},
      })
      .pipe(mapToModel(this.model, 'data'));
  }

  upload(customerId: number, data: string, name: string) {
    return this.http.post<Document>(`${this.endpoint}/upload`, {
      customerId,
      data,
      name,
    });
  }
}
